import React, { useEffect, useRef } from 'react';
import anime from 'animejs';
import ShapeGrid from './ShapeGrid';
import FloatingShapes from './FloatingShapes';
import SplitText from './SplitText';
import Magnetic from './Magnetic';
import ClickBurst from './ClickBurst';

// Reusable anime.js hero shell used by every service page.
// Pass `title` as array of { text, gradient }, `pill`, `desc`, `actions` array of { label, to, primary, icon, external, onClick }.
const PageHero = ({ pill, title = [], desc, actions = [], children, minH = '78vh' }) => {
    const heroRef = useRef(null);
    const titleRef = useRef(null);
    const descRef = useRef(null);
    const actionsRef = useRef(null);
    const childrenRef = useRef(null);

    useEffect(() => {
        const tl = anime.timeline({ easing: 'easeOutCubic' });
        if (titleRef.current) {
            tl.add({
                targets: titleRef.current.querySelectorAll('.char'),
                opacity: [0, 1],
                translateY: [-40, 0],
                rotateZ: [-15, 0],
                scale: [0.7, 1],
                duration: 700,
                delay: anime.stagger(18),
            }, 0);
        }
        if (descRef.current) {
            tl.add({
                targets: descRef.current,
                opacity: [0, 1],
                translateY: [20, 0],
                duration: 700,
            }, '-=200');
        }
        if (actionsRef.current) {
            tl.add({
                targets: actionsRef.current.querySelectorAll('.cta-item'),
                opacity: [0, 1],
                translateY: [20, 0],
                scale: [0.92, 1],
                duration: 600,
                delay: anime.stagger(80),
                easing: 'easeOutBack',
            }, '-=300');
        }
        if (childrenRef.current) {
            tl.add({
                targets: childrenRef.current,
                opacity: [0, 1],
                translateY: [30, 0],
                duration: 700,
            }, '-=200');
        }

        // Continuous title breathing once entrance is over
        const breathTimer = setTimeout(() => {
            if (!titleRef.current) return;
            anime({
                targets: titleRef.current.querySelectorAll('.char'),
                translateY: [0, -4, 0],
                duration: 4000,
                loop: true,
                delay: anime.stagger(40),
                easing: 'easeInOutSine',
            });
        }, 1800);
        return () => clearTimeout(breathTimer);
    }, []);

    return (
        <section
            ref={heroRef}
            className="relative bg-[#05060a] text-white overflow-hidden"
            style={{ perspective: '1200px', minHeight: minH }}
        >
            <div className="absolute inset-0 bg-grid opacity-40" aria-hidden="true" />
            <div className="absolute inset-0 spotlight" aria-hidden="true" />
            <ShapeGrid density={44} />
            <FloatingShapes />
            <ClickBurst targetRef={heroRef} />
            <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-[#05060a] z-10" aria-hidden="true" />

            <div className="relative z-20 px-6 md:px-16 pt-24 pb-20 max-w-7xl mx-auto flex flex-col justify-center" style={{ minHeight: minH }}>
                {pill && (
                    <div className="flex items-center gap-3 mb-8">
                        <span className="pill tag"><span className="pulse-dot" /> {pill}</span>
                    </div>
                )}
                <h1
                    ref={titleRef}
                    className="font-display text-5xl md:text-7xl lg:text-[5.4rem] font-bold leading-[1.02] mb-6 max-w-5xl"
                >
                    {title.map((line, i) => (
                        <span key={i} className={`block ${line.gradient ? 'text-gradient-red' : ''}`}>
                            <SplitText text={line.text} />
                        </span>
                    ))}
                </h1>
                {desc && (
                    <p ref={descRef} className="text-gray-300 max-w-3xl mb-8 text-lg leading-relaxed">
                        {desc}
                    </p>
                )}
                {actions.length > 0 && (
                    <div ref={actionsRef} className="flex flex-wrap gap-3 mb-10">
                        {actions.map((a, i) => (
                            <span key={i} className="cta-item">
                                <Magnetic>
                                    {a.external ? (
                                        <a href={a.to} target="_blank" rel="noreferrer" className={a.primary ? 'btn-primary' : 'btn-ghost'}>
                                            {a.icon} {a.label}
                                        </a>
                                    ) : (
                                        <a href={a.to} onClick={a.onClick} className={a.primary ? 'btn-primary cursor-pointer' : 'btn-ghost cursor-pointer'}>
                                            {a.icon} {a.label}
                                        </a>
                                    )}
                                </Magnetic>
                            </span>
                        ))}
                    </div>
                )}
                {children && (
                    <div ref={childrenRef}>{children}</div>
                )}
            </div>
        </section>
    );
};

export default PageHero;
